import React, { useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Button,
  Stack,
  Grid,
  CircularProgress,
  Alert,
  Typography
} from '@mui/material';
import { Formik, Form, Field } from 'formik';
import { Icon } from '@iconify/react';
import sendFill from '@iconify/icons-eva/paper-plane-fill';
import saveFill from '@iconify/icons-eva/save-fill';
import refreshFill from '@iconify/icons-eva/refresh-fill';
// validation schemas
import { getCommandValidationSchema } from 'src/utils/validationSchemas';
// constants
import {
  OCPI_COMMAND_TYPES,
  OCPI_COMMANDS,
  getCommandFormInitialValues
} from 'src/utils/ocpi.constants';
// hooks
import { useSendCommand } from 'src/hooks/useOcpiCommands.hook';
import { useList } from 'src/hooks/useFetch.hook';
// components
import { Loader } from 'src/components/Loader';

export default function CommandForm({
  selectedType,
  onTypeChange,
  formData,
  onFormChange,
  onSubmit,
  onSaveAsTemplate,
  onClear,
  onRedirectToOverview
}) {
  const sendCommandMutation = useSendCommand("sendCommand");
  const { hasError, refetch, data, isLoading } = useList("commandBuilderData", false);

  const validationSchema = getCommandValidationSchema(selectedType);
  const initialValues = getCommandFormInitialValues(selectedType);

  // Only update formData when selectedType changes (not on every render)
  const prevType = useRef();
  useEffect(() => {
    if (selectedType && prevType.current !== selectedType) {
      const template = getCommandFormInitialValues(selectedType);
      onFormChange(template);
      prevType.current = selectedType;
    }
  }, [selectedType, onFormChange]);

  // --- FIX: useEffect to sync form values with parent ---
  const formikValuesRef = useRef();
  const prevValuesRef = useRef();

  useEffect(() => {
    if (formikValuesRef.current && formikValuesRef.current !== prevValuesRef.current) {
      onFormChange(formikValuesRef.current);
      prevValuesRef.current = formikValuesRef.current;
    }
  }, [onFormChange]);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      // Use the API mutation
      let moduleData;

      if ([OCPI_COMMANDS.START_SESSION, OCPI_COMMANDS.RESERVE_NOW, OCPI_COMMANDS.UNLOCK_CONNECTOR].includes(selectedType)) {
        moduleData = data.locations.find(token => token.id === values.location_id);
      } else if (selectedType === OCPI_COMMANDS.RESERVE_NOW) {
        moduleData = data.tokens.find(token => token.uid === values.token_id);
      } else if (selectedType === OCPI_COMMANDS.STOP_SESSION) {
        moduleData = data.sessions.find(session => session.id === values.session_id);
      }
      values.party_id = moduleData.party_id;
      values.country_code = moduleData.country_code;
      await sendCommandMutation.mutateAsync(values);

      // Redirect to Overview tab after successful command sending
      if (onRedirectToOverview) {
        onRedirectToOverview();
      }
      setSubmitting(false);
      resetForm();
    } catch (error) {
      setSubmitting(false);
      console.error('Failed to send command:', error);
    }
  };

  const handleCommandTypeChange = (setFieldValue) => (e) => {
    onTypeChange(e.target.value);
    setFieldValue('command_type', e.target.value);
    refetch();
  }

  const renderTypeSpecificFields = (values, setFieldValue) => {
    switch (selectedType) {
      case 'START_SESSION':
        return (<>
          <Grid item xs={12} md={6}>
            <Field name="evse_uid">
              {({ field, meta }) => (
                <TextField
                  {...field}
                  label="EVSE UID"
                  size="small"
                  fullWidth
                  error={meta.touched && !!meta.error}
                  helperText={meta.touched && meta.error}
                  placeholder="Enter EVSE UID"
                />
              )}
            </Field>
          </Grid>

          <Grid item xs={12} md={6}>
            <Field name="connector_id">
              {({ field, meta }) => (
                <TextField
                  {...field}
                  label="Connector ID"
                  size="small"
                  fullWidth
                  error={meta.touched && !!meta.error}
                  helperText={meta.touched && meta.error}
                  placeholder="Enter connector ID"
                />
              )}
            </Field>
          </Grid>

          <Grid item xs={12}>
            <Field name="authorization_reference">
              {({ field, meta }) => (
                <TextField
                  {...field}
                  label="Authorization Reference"
                  size="small"
                  fullWidth
                  error={meta.touched && !!meta.error}
                  helperText={meta.touched && meta.error}
                  placeholder="Enter authorization reference"
                />
              )}
            </Field>
          </Grid>
        </>);

      case 'STOP_SESSION':
        return (
        <Grid item xs={12}>
          <Field name="session_id">
            {({ field, meta }) => (
              <TextField
                {...field}
                select
                label="Session ID"
                size="small"
                fullWidth
                required
                error={meta.touched && !!meta.error}
                helperText={meta.touched && meta.error}
                onChange={(e) => {
                  setFieldValue('session_id', e.target.value);
                }}
              >
                <MenuItem value="">Select session</MenuItem>
                {data && data.sessions.map((session) => (
                  <MenuItem key={session.id} value={session.id}>
                    Session Id: {session.id} | Location Id: {session.location_id}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Field>
        </Grid>
        );

      case 'UNLOCK_CONNECTOR':
        return (
          <>
            <Grid item xs={12} md={6}>
              <Field name="evse_uid">
                {({ field, meta }) => (
                  <TextField
                    {...field}
                    label="EVSE UID"
                    size="small"
                    fullWidth
                    error={meta.touched && !!meta.error}
                    helperText={meta.touched && meta.error}
                    placeholder="Enter EVSE UID"
                  />
                )}
              </Field>
            </Grid>

            <Grid item xs={12}>
              <Field name="connector_id">
                {({ field, meta }) => (
                  <TextField
                    {...field}
                    label="Connector ID"
                    size="small"
                    fullWidth
                    error={meta.touched && !!meta.error}
                    helperText={meta.touched && meta.error}
                    placeholder="Enter connector ID"
                  />
                )}
              </Field>
            </Grid>
          </>
        );

      case 'RESERVE_NOW':
        return (
         <>
            <Grid item xs={12} md={6}>
              <Field name="evse_uid">
                {({ field, meta }) => (
                  <TextField
                    {...field}
                    label="EVSE UID"
                    size="small"
                    fullWidth
                    error={meta.touched && !!meta.error}
                    helperText={meta.touched && meta.error}
                    placeholder="Enter EVSE UID"
                  />
                )}
              </Field>
            </Grid>

            <Grid item xs={12} md={6}>
              <Field name="authorization_reference">
                {({ field, meta }) => (
                  <TextField
                    {...field}
                    label="Authorization Reference"
                    size="small"
                    fullWidth
                    error={meta.touched && !!meta.error}
                    helperText={meta.touched && meta.error}
                    placeholder="Enter authorization reference"
                  />
                )}
              </Field>
            </Grid>

            <Grid item xs={12} md={6}>
              <Field name="reservation_id">
                {({ field, meta }) => (
                  <TextField
                    {...field}
                    label="Reservation ID"
                    size="small"
                    fullWidth
                    error={meta.touched && !!meta.error}
                    helperText={meta.touched && meta.error}
                    placeholder="Enter reservation ID"
                  />
                )}
              </Field>
            </Grid>
          </>
        );

      case 'CANCEL_RESERVATION':
        return (
          <>
            <Grid item xs={12} md={6}>
              <Field name="reservation_id">
                {({ field, meta }) => (
                  <TextField
                    {...field}
                    label="Reservation ID"
                    size="small"
                    fullWidth
                    error={meta.touched && !!meta.error}
                    helperText={meta.touched && meta.error}
                    placeholder="Enter reservation ID to cancel"
                  />
                )}
              </Field>
            </Grid>
          </>
        );

      default:
        return null;
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
        <CircularProgress />
        <Box sx={{ ml: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Loading command builder data...
          </Typography>
        </Box>
      </Box>
    );
  }

  // Show error state
  if (hasError) {
    return (
      <Box sx={{ py: 2 }}>
        <Alert 
          severity="error" 
          action={
            <Button color="inherit" size="small" onClick={refetch}>
              Retry
            </Button>
          }
        >
          Failed to load command builder data. Please try again.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Loading Overlay */}
      <Loader
        variant="top"
        open={sendCommandMutation.isLoading}
      />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, errors, touched, isValid, isSubmitting }) => {
          // Store latest values in ref for top-level effect
          formikValuesRef.current = values;

          return (
            <Form>
              <Grid container spacing={3}>
                {/* Command Type */}
                <Grid item xs={12}>
                  <TextField
                    select
                    label="Command Type"
                    size="small"
                    value={selectedType}
                    onChange={handleCommandTypeChange(setFieldValue)}
                    fullWidth
                    required
                  >
                    <MenuItem value="">Select command type</MenuItem>
                    {OCPI_COMMAND_TYPES.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            px: 2,
                            py: 0.5,
                            borderRadius: 2,
                            bgcolor: `${type.color}22`,
                            color: type.color,
                            fontWeight: 500,
                            fontSize: 14,
                            border: `1px solid ${type.color}55`,
                            width: 'fit-content',
                            minWidth: 120,
                            justifyContent: 'center',
                          }}
                        >
                          {type.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {selectedType && (
                  <>
                    {/* Location */}
                    {selectedType !== OCPI_COMMANDS.STOP_SESSION && selectedType !== OCPI_COMMANDS.CANCEL_RESERVATION && (
                      <>
                        <Grid item xs={12} md={6}>
                          <Field name="location_id">
                            {({ field, meta }) => (
                              <TextField
                                {...field}
                                select
                                label="Location"
                                size="small"
                                fullWidth
                                required
                                error={meta.touched && !!meta.error}
                                helperText={meta.touched && meta.error}
                                onChange={(e) => {
                                  setFieldValue('location_id', e.target.value);
                                  setFieldValue('evse_uid', ''); // Reset EVSE when location changes
                                }}
                              >
                                <MenuItem value="">Select location</MenuItem>
                                {data && data.locations.map((location) => (
                                  <MenuItem key={location.id} value={location.id} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>{location.name} ({location.id})</span>
                                    <Box
                                      component="span"
                                      sx={{
                                        ml: 1,
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: 1,
                                        bgcolor: 'primary.lighter',
                                        color: 'primary.dark',
                                        fontSize: '0.75rem',
                                        float: 'right'
                                      }}
                                    >
                                      {location.party_id}
                                    </Box>
                                  </MenuItem>
                                ))}
                              </TextField>
                            )}
                          </Field>
                        </Grid>

                        {/* Token */}
                        {![OCPI_COMMANDS.UNLOCK_CONNECTOR].includes(selectedType) && 
                        <Grid item xs={12} md={6}>
                          <Field name="token_id">
                            {({ field, meta }) => (
                              <TextField
                                {...field}
                                select
                                label="Token"
                                size="small"
                                fullWidth
                                required
                                error={meta.touched && !!meta.error}
                                helperText={meta.touched && meta.error}
                                onChange={(e) => {
                                  setFieldValue('token_id', e.target.value);
                                  setFieldValue('evse_uid', ''); // Reset EVSE when location changes
                                }}
                              >
                                <MenuItem value="">Select Token</MenuItem>
                                {data && data.tokens.map((token) => (
                                  <MenuItem key={token.uid} value={token.uid} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>{token.uid} ({token.type})</span>
                                    <Box
                                      component="span"
                                      sx={{
                                        ml: 1,
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: 1,
                                        bgcolor: 'primary.lighter', 
                                        color: 'primary.dark',
                                        fontSize: '0.75rem',
                                        float: 'right'
                                      }}
                                    >
                                      {token.party_id}
                                    </Box>
                                  </MenuItem>
                                ))}
                              </TextField>
                            )}
                          </Field>
                        </Grid>}
                      </>
                    )}

                    {/* Type-specific fields */}
                    {renderTypeSpecificFields(values, setFieldValue)}

                    {/* Notes */}
                    <Grid item xs={12}>
                      <Field name="notes">
                        {({ field, meta }) => (
                          <TextField
                            {...field}
                            label="Notes (Optional)"
                            size="small"
                            fullWidth
                            multiline
                            rows={3}
                            placeholder="Additional notes for this command..."
                            error={meta.touched && !!meta.error}
                            helperText={meta.touched && meta.error}
                          />
                        )}
                      </Field>
                    </Grid>

                    {/* Action Buttons */}
                    <Grid item xs={12}>
                      <Stack direction="row" spacing={2} sx={{ pt: 2 }}>
                        <Button
                          type="submit"
                          variant="contained"
                          startIcon={<Icon icon={sendFill} width={16} height={16} />}
                          disabled={!isValid || isSubmitting || sendCommandMutation.isPending}
                          color="primary"
                        >
                          Send Command
                        </Button>

                        <Button
                          variant="outlined"
                          startIcon={<Icon icon={refreshFill} width={16} height={16} />}
                          onClick={onClear}
                          disabled={sendCommandMutation.isPending}
                        >
                          Reset
                        </Button>

                        <Button
                          variant="outlined"
                          startIcon={<Icon icon={saveFill} width={16} height={16} />}
                          onClick={onSaveAsTemplate}
                          disabled={!isValid || sendCommandMutation.isPending}
                        >
                          Save Template
                        </Button>
                      </Stack>
                    </Grid>
                  </>
                )}
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
}
